import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import SelectionPanel from 'shared/components/configPanel/SelectionPanel';
import ActionSectionForm, {
  TestActionProps,
} from 'shared/components/configPanel/ActionSectionForm';
import { SectionPrompt } from 'shared/components/configPanel/UnsavedSectionPrompt';
import HowToConnect from 'shared/components/configPanel/HowToConnect';
import { useConfigEditor } from 'shared/components/configPanel/useConfigEditor';
import {
  isAuth,
  isDormantAction,
} from 'shared/components/configPanel/actionSetup';
import { flowActionCounts } from 'shared/components/configPanel/buildActionNav';
import { buttonClasses } from 'styles/tailwindClassnames';
import { OpenInNewIcon } from 'shared/components/ui/ProjectIcons';
import SharedAccountNotice from 'routes/SharedAccountNotice';
import RouteDetailsWebhook, {
  RouteDetailsWebhookHandle,
} from 'routes/RouteDetailsWebhook';
import { AccountBundle, Selection } from '../flowMapTypes';
import { FlowArrow } from '../useFlowGraph';
import CopyIdButton from './CopyIdButton';
import PanelSkeleton from './PanelSkeleton';
import AccountGeneralForm from './AccountGeneralForm';
import { Confirmation } from './ConfirmationModal';
import { accountNav } from './accountNav';
import { GeneralDraftState } from './useGeneralDraft';
import { SharingRoute } from './useSharedBySite';

type WebhookProps = React.ComponentProps<typeof RouteDetailsWebhook>;

type AccountSelection = Extract<Selection, { kind: 'account' }>;

export type AccountPanelProps = {
  selection: AccountSelection;
  select: (next: Selection) => void;
  bundles: Record<string, AccountBundle>;
  destinations: any[];
  arrows: FlowArrow[];
  providerId: string;
  routeProvider: any;
  routeEnabled: boolean;
  token: string | undefined;
  currentMemberIsAdmin: boolean;
  editor: ReturnType<typeof useConfigEditor>;
  general: GeneralDraftState;
  apiKeys: Record<string, string>;
  sharedBySite: Record<string, SharingRoute[]>;
  confirm: (confirmation: Confirmation) => void;
  prompt: (prompt: SectionPrompt) => void;
  guardedNavigate: (to: string) => void;
  // Held by the tab, so a save still running survives a trip to an arrow.
  savingWebhook: boolean;
  setSavingWebhook: (saving: boolean) => void;
  // Passed through from the Route page.
  providerSharedWith?: SharingRoute[];
  onRemoveSite: (dest: any) => unknown;
  onTestAction?: TestActionProps['onTestAction'];
  testActionLoading?: boolean;
  testActionResponse?: any;
  onSaveWebhook: WebhookProps['onSave'];
  onCancelWebhook: WebhookProps['onCancel'];
  webhookChangesMade: WebhookProps['changesMade'];
  onWebhookChangesMade: WebhookProps['onChangesMade'];
  organizations?: any[];
  onLoadMoreOrgs?: () => void;
  onSearchOrgs?: (search: string) => void;
};

// The panel for one account on the map: its General fields, every action it
// owns, its webhook payload parsing and its API key.
const AccountPanel = (props: AccountPanelProps) => {
  const { selection, select, bundles, editor, general } = props;
  const { t } = useTranslation();
  // The webhook editor holds its own draft; the panel footer drives its save.
  const webhookRef = useRef<RouteDetailsWebhookHandle>(null);

  const bundle = bundles[selection.integrationId];
  if (!bundle) return <PanelSkeleton />;
  const dest = props.destinations.find((d: any) => d.id === bundle.id);
  // Always set here: the panel only ever shows the selected account.
  const urlState = general.baseUrl!;
  const {
    nav,
    dormant,
    exemptSlugs,
    routeKind,
    webhookConfig,
    activeSlug,
    activeAction,
  } = accountNav({
    bundle,
    urlState,
    arrows: props.arrows,
    providerId: props.providerId,
    routeProvider: props.routeProvider,
    bundles,
    destinations: props.destinations,
    section: selection.section,
    t,
  });
  // Saving is per section, so Save/Cancel act on the active section only —
  // the section-switch prompt guarantees no other section is dirty.
  const isDirty =
    !!activeAction && editor.isActionDirty(bundle.id, activeAction.id);
  const onWebhook = activeSlug === 'webhook' && !!webhookConfig;
  // An action that was never saved, or that is still missing required
  // fields, can be committed with the values on screen, so Save is live
  // even before the user pokes a field.
  // The editor's own helper, not actionCanCommitForm directly: it adds the
  // "nothing to commit for a never-saved section that is switched off"
  // case, and a second copy of the rule here went out of step with it.
  const canCommitDefaults = editor.canCommitDefaults(bundle, activeAction);
  const { fields } = general;

  // Section switches inside the account ask the Save-or-discard question
  // whenever the section being left has unsaved work.
  const goToSection = (slug: string) =>
    select({ kind: 'account', integrationId: bundle.id, section: slug });
  const selectSection = (slug: string) => {
    if (slug === activeSlug) return;
    if (activeSlug === 'general' && fields.dirty) {
      return props.prompt({
        onSave: general.save,
        onDiscard: general.discard,
        proceed: () => goToSection(slug),
      });
    }
    if (activeAction && editor.isActionDirty(bundle.id, activeAction.id)) {
      return props.prompt({
        onSave: () => editor.save(bundle, activeAction.id),
        onDiscard: () => editor.cancel(bundle, activeAction.id),
        proceed: () => goToSection(slug),
      });
    }
    goToSection(slug);
  };

  const saveCancel = (
    save: { disabled: boolean; onClick: () => void; testId?: string },
    cancel: { disabled: boolean; onClick: () => void; testId?: string },
  ) => (
    <>
      <button
        className={buttonClasses.primaryAction}
        disabled={save.disabled}
        data-testid={save.testId}
        onClick={save.onClick}
      >
        {t('Save')}
      </button>
      <button
        className={buttonClasses.cancelAction}
        disabled={cancel.disabled}
        data-testid={cancel.testId}
        onClick={cancel.onClick}
      >
        {t('Cancel')}
      </button>
    </>
  );

  const footer = onWebhook
    ? props.currentMemberIsAdmin
      ? // This editor keeps its own draft, so the panel reaches the save
        // through a handle rather than lifting all of it.
        saveCancel(
          {
            disabled: !props.webhookChangesMade || props.savingWebhook,
            testId: 'RouteDetailsWebhook-save-button',
            onClick: async () => {
              props.setSavingWebhook(true);
              try {
                await webhookRef.current?.save();
              } finally {
                props.setSavingWebhook(false);
              }
            },
          },
          {
            disabled: !props.webhookChangesMade,
            testId: 'RouteDetailsWebhook-cancel-button',
            onClick: () => props.onCancelWebhook?.(),
          },
        )
      : undefined
    : activeSlug === 'general'
      ? saveCancel(
          {
            disabled:
              !fields.dirty ||
              !!fields.nameError ||
              !!fields.ownerError ||
              !!fields.urlError ||
              general.saving,
            onClick: general.save,
          },
          { disabled: !general.hasDraft, onClick: general.discard },
        )
      : activeAction
        ? saveCancel(
            {
              disabled: !isDirty && !canCommitDefaults,
              onClick: () => {
                editor.commitDefaults(bundle, activeAction);
                editor.save(bundle, activeAction.id);
              },
            },
            {
              disabled: !isDirty,
              onClick: () => editor.cancel(bundle, activeAction.id),
            },
          )
        : undefined;

  return (
    <SelectionPanel
      title={
        <span className="flex min-w-0 flex-col gap-1">
          <span className="truncate">{bundle.name}</span>
          <span className="flex items-center gap-8 text-body-sm font-normal">
            {/* The account's own screen: every Route using it, and the
                record itself outside this Route's context. Routed through
                the same Save-or-discard prompt as a selection change. */}
            <button
              type="button"
              data-testid="ConfigurationsTab-view-connection-button"
              onClick={() =>
                props.guardedNavigate(`/connections/${bundle.id}/configuration`)
              }
              className={`${buttonClasses.inlineLink} flex items-center gap-1 no-underline`}
            >
              {t('View Connection')}
              <OpenInNewIcon />
            </button>
            <CopyIdButton
              key={bundle.id}
              id={bundle.id}
              testId="ConfigurationsTab-copy-id-button"
              label={t('Copy Connection ID')}
            />
          </span>
        </span>
      }
      headerActions={
        <>
          {!bundle.isProvider && dest && (
            <button
              className={buttonClasses.headerActionRed}
              onClick={() =>
                props.confirm({
                  title: t('Remove from Route'),
                  body: t(
                    'Remove {{name}} from this Route? The site itself is not deleted — it just stops receiving data from this flow.',
                    { name: bundle.name },
                  ),
                  confirmLabel: t('Remove'),
                  destructive: true,
                  onConfirm: () => {
                    Promise.resolve(props.onRemoveSite(dest)).then(() =>
                      select({ kind: 'none' }),
                    );
                  },
                })
              }
            >
              {t('Remove from Route')}
            </button>
          )}
        </>
      }
      banner={
        // The API key section only shows connection details; nothing there
        // can change what other Routes use.
        activeSlug !== 'how-to-connect' && (
          <SharedAccountNotice
            // Keyed per account, so a list expanded on one account starts
            // collapsed again on the next.
            key={bundle.id}
            usedBy={
              bundle.isProvider
                ? props.providerSharedWith || []
                : props.sharedBySite[bundle.id] || []
            }
          />
        )
      }
      nav={nav}
      activeSlug={activeSlug}
      onSelectSection={selectSection}
      emptyNavNote={t('This account has no general configuration.')}
      footer={footer}
    >
      {activeSlug === 'general' ? (
        <AccountGeneralForm
          fields={fields}
          urlState={urlState}
          onEdit={general.edit}
          organizations={props.organizations}
          onLoadMoreOrgs={props.onLoadMoreOrgs}
          onSearchOrgs={props.onSearchOrgs}
        />
      ) : activeSlug === 'how-to-connect' ? (
        <HowToConnect
          kind={bundle.isProvider ? routeKind : 'api-push'}
          integrationType={
            (bundle.isProvider
              ? props.routeProvider?.type?.value
              : dest?.type?.value) || ''
          }
          apiKey={props.apiKeys[bundle.id]}
          isAdmin={props.currentMemberIsAdmin}
        />
      ) : activeSlug === 'webhook' && webhookConfig ? (
        <RouteDetailsWebhook
          // The footer's Save reaches the editor's draft through
          // this handle.
          ref={webhookRef}
          webhookConfiguration={webhookConfig}
          currentMemberIsAdmin={props.currentMemberIsAdmin}
          onSave={props.onSaveWebhook}
          onCancel={props.onCancelWebhook}
          changesMade={props.webhookChangesMade}
          onChangesMade={props.onWebhookChangesMade}
        />
      ) : activeAction ? (
        <>
          {isDormantAction(activeAction, dormant) && (
            /* This direction isn't carrying data, so nothing here is
               required yet — say so, or the empty required fields read as
               work the user owes right now. Two reasons a direction can be
               dormant, two different truths: a paused Route's main
               direction is not "the other direction". */
            <p className="mb-3 rounded border border-ui-divider-lines px-3 py-2 text-body-sm text-ui-text-primary-text">
              {props.routeEnabled
                ? t(
                    'These settings are not required for the current data flow. Configure them only if you enable data flowing in the other direction.',
                  )
                : t(
                    "This Route is paused — these settings apply when it's turned back on.",
                  )}
            </p>
          )}
          {/* `showRequiredHints` follows the same rule as the nav "!" badge: a
              section the nav doesn't badge must not paint red "Required"
              hints either. */}
          <ActionSectionForm
            target={bundle}
            action={activeAction}
            editable={true}
            showRequiredHints={
              isAuth(activeAction) ||
              flowActionCounts(activeAction, dormant, exemptSlugs)
            }
            userIsAdmin={props.currentMemberIsAdmin}
            token={props.token}
            formVersion={editor.formVersion[bundle.id] || 0}
            errors={editor.formErrors[bundle.id]?.[activeAction.id]}
            formData={editor.formDataFor(bundle, activeAction)}
            getFormData={() => editor.latestFormDataFor(bundle, activeAction)}
            onChange={(data: any, fieldId?: string) =>
              editor.snapshotChange(bundle, activeAction.id, data, fieldId)
            }
            pendingEdits={{
              hasEdits: () => editor.isActionDirty(bundle.id, activeAction.id),
              save: () => editor.save(bundle, activeAction.id),
              discard: () => editor.cancel(bundle, activeAction.id),
            }}
            test={
              props.onTestAction
                ? {
                    onTestAction: props.onTestAction,
                    loading: !!props.testActionLoading,
                    response: props.testActionResponse,
                  }
                : undefined
            }
          />
        </>
      ) : (
        <p className="text-body-sm text-ui-text-primary-text">
          {t('This account has no general configuration.')}
        </p>
      )}
    </SelectionPanel>
  );
};

export default AccountPanel;
